import React, { useCallback, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { loadAllDataFromSupabase } from "./TagTransactions/utils/storageUtils";

const FileUpload = ({ onCsvDataLoaded }) => {
  const [validationStatus, setValidationStatus] = useState("pending"); // 'pending', 'validating', 'valid', 'invalid'
  const [validationErrors, setValidationErrors] = useState([]);
  const [transactionCount, setTransactionCount] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [isLoadingFromDatabase, setIsLoadingFromDatabase] = useState(false);
  const [hasDatabaseData, setHasDatabaseData] = useState(false);
  const [checkingDatabase, setCheckingDatabase] = useState(true);

  useEffect(() => {
    const savedData = localStorage.getItem("transactionData");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          setHasExistingData(true);
        }
      } catch (e) {
        console.error("Error checking existing data:", e);
      }
    }
  }, []);

  // Check if database has data on component mount
  useEffect(() => {
    const checkDatabaseData = async () => {
      try {
        const userData = await loadAllDataFromSupabase();
        if (userData && userData.transactions && userData.transactions.length > 0) {
          setHasDatabaseData(true);
        }
      } catch (error) {
        console.log('No database data available or user not logged in:', error.message);
        setHasDatabaseData(false);
      } finally {
        setCheckingDatabase(false);
      }
    };

    checkDatabaseData();
  }, []);

  const transformDate = useCallback((dateStr) => {
    // Check if the date is in dd/MM/yyyy format
    const ddMMyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateStr.match(ddMMyyyyRegex);

    if (match) {
      // Extract day, month, and year from the matched groups
      const [, day, month, year] = match;
      // Return in yyyy-MM-dd format
      return `${year}-${month}-${day}`;
    }

    // If not in dd/MM/yyyy format, return as is (assuming it's already in yyyy-MM-dd)
    return dateStr;
  }, []);

  // Validate the CSV data
  const validateData = useCallback((data, headers) => {
    const errors = [];

    // Check required columns
    const requiredColumns = ["date", "amount", "description"];
    const missingColumns = requiredColumns.filter(
      (col) => !headers.includes(col),
    );

    if (missingColumns.length > 0) {
      errors.push(`Missing required columns: ${missingColumns.join(", ")}`);
    }

    // Validate data in each row
    data.forEach((row, index) => {
      // Check date format
      if (row.date) {
        if (new Date(row.date).toString() === "Invalid Date") {
          errors.push(
            `Row ${index + 1}: Invalid date value: ${row.date}. Please use YYYY-MM-DD format, e.g. 2025-05-30`,
          );
        }
        if (
          new Date(row.date) < new Date("1900-1-1") ||
          new Date(row.date) > new Date("2999-12-31")
        ) {
          errors.push(`Row ${index + 1}: Invalid date range: ${row.date}`);
        }
      } else if (headers.includes("date")) {
        errors.push(`Row ${index + 1}: Missing date value`);
      }

      // Check amount is a number
      if (row.amount) {
        if (isNaN(parseFloat(row.amount))) {
          errors.push(
            `Row ${index + 1}: Amount is not a number: ${row.amount}`,
          );
        }
      } else if (headers.includes("amount")) {
        errors.push(`Row ${index + 1}: Missing amount value`);
      }

      // Check description exists
      if (!row.description && headers.includes("description")) {
        errors.push(`Row ${index + 1}: Missing description value`);
      }
    });

    return errors;
  }, []);

  const processData = useCallback(
    (text) => {
      setIsProcessing(true);
      setValidationStatus("validating");

      try {
        const lines = text.split("\n");
        const headers = lines[0].split(",").map((h) => h.trim());

        const data = [];
        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim() === "") continue;

          const values = lines[i].split(",").map((v) => v.trim());
          const row = {};
          headers.forEach((header, index) => {
            // Transform date format if the column is 'date'
            if (header === "date") {
              row[header] = transformDate(values[index]);
            } else {
              row[header] = values[index];
            }
          });
          data.push(row);
        }

        // Validate the data
        const errors = validateData(data, headers);

        if (errors.length > 0) {
          setValidationStatus("invalid");
          setValidationErrors(errors);
        } else {
          setValidationStatus("valid");
          setValidationErrors([]);
          setParsedData(data);
          setTransactionCount(data.length);

          // Clear all existing data from localStorage
          localStorage.removeItem("changeHistory");
          localStorage.removeItem("reportSettings");
          localStorage.removeItem("tableFilters");

          // Store the transformed data in localStorage
          localStorage.setItem("transactionData", JSON.stringify(data));

          setHasExistingData(false);
        }
      } catch (error) {
        setValidationStatus("invalid");
        setValidationErrors([`Error processing file: ${error.message}`]);
      } finally {
        setIsProcessing(false);
      }
    },
    [transformDate, validateData],
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();

        reader.onload = (e) => {
          const text = e.target.result;
          processData(text);
        };

        reader.readAsText(file);
      }
    },
    [processData],
  );

  const handleProceed = () => {
    if (parsedData && validationStatus === "valid") {
      onCsvDataLoaded(parsedData);
    }
  };

  const handleLoadFromDatabase = async () => {
    setIsLoadingFromDatabase(true);
    try {
      const userData = await loadAllDataFromSupabase();

      if (
        userData &&
        userData.transactions &&
        userData.transactions.length > 0
      ) {
        // Process the transactions data for validation
        const headers = Object.keys(userData.transactions[0]);
        const errors = validateData(userData.transactions, headers);

        if (errors.length > 0) {
          setValidationStatus("invalid");
          setValidationErrors(errors);
        } else {
          setValidationStatus("valid");
          setValidationErrors([]);
          setParsedData(userData.transactions);
          setTransactionCount(userData.transactions.length);

          // Save all data to localStorage to replace any existing data
          localStorage.setItem("transactionData", JSON.stringify(userData.transactions));
          
          if (userData.filters) {
            localStorage.setItem("tableFilters", JSON.stringify(userData.filters));
          }
          
          if (userData.userSettings) {
            localStorage.setItem("userSettings", JSON.stringify(userData.userSettings));
          }
          
          // Store rules and history for managers to load
          if (userData.rules) {
            localStorage.setItem("savedRules", JSON.stringify(userData.rules));
          }
          
          if (userData.history) {
            localStorage.setItem("savedHistory", JSON.stringify(userData.history));
          }
          
          // Update existing data state
          setHasExistingData(true);
          
          // Notify parent about the complete data load
          if (onCsvDataLoaded) {
            onCsvDataLoaded(userData.transactions, {
              rules: userData.rules || [],
              history: userData.history || [],
              userSettings: userData.userSettings || {},
              reportSettings: userData.reportSettings || {},
              filters: userData.filters || {}
            });
            return; // Don't proceed to regular flow since we're handling it here
          }
        }
      } else {
        setValidationStatus("invalid");
        setValidationErrors(["No transaction data found in database"]);
      }
    } catch (error) {
      setValidationStatus("invalid");
      setValidationErrors([`Error loading from database: ${error.message}`]);
    } finally {
      setIsLoadingFromDatabase(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  // Check for existing data in localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("transactionData");
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        if (Array.isArray(data) && data.length > 0) {
          // Get headers from the first object's keys
          const headers = Object.keys(data[0]);

          // Validate the existing data
          const errors = validateData(data, headers);

          if (errors.length > 0) {
            setValidationStatus("invalid");
            setValidationErrors(errors);
          } else {
            setValidationStatus("valid");
            setValidationErrors([]);
            setParsedData(data);
            setTransactionCount(data.length);
          }
        }
      } catch (error) {
        setValidationStatus("invalid");
        setValidationErrors([`Error loading saved data: ${error.message}`]);
      }
    }
  }, [validateData]);

  // Modify the drop zone message based on existing data
  const getDropzoneMessage = () => {
    if (hasExistingData) {
      return (
        <>
          <Typography variant="h6" gutterBottom color="warning.main">
            Warning: Uploading a new file will reset all existing tags,
            comments, and tax information
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Drag and drop a new CSV file here, or click to select
          </Typography>
        </>
      );
    }
    return (
      <>
        <Typography variant="h6" gutterBottom>
          {isDragActive
            ? "Drop the file here"
            : "Drag and drop a CSV file here, or click to select"}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Only single CSV file is allowed
        </Typography>
      </>
    );
  };

  const description = `
    Please upload your bank transaction data in CSV format.
    ** Everything happens on your local computer until you click "Save All" button
    ** If you "Save All", you'll be able to save all tags / tax info / comments as well as histories and rules for future use

    Accepted columns:
    - date: Transaction date - in a standard format (YYYY-MM-DD)
    - amount: Transaction amount, can be positive or negative
    - description: Main transaction description. Please don't include "," in it
    - description2: (optional) Additional details about the transaction

    File requirements:
    - Only .csv files are accepted
    - File must contain headers matching the exact column names above
  `;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Upload Bank Transactions
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, whiteSpace: "pre-line" }}>
        {description}
      </Typography>

      {/* Database Load Option - only show if there's data in database */}
      {!checkingDatabase && hasDatabaseData && (
        <>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: "primary.50",
              border: "1px solid",
              borderColor: "primary.200",
            }}
          >
            <Typography variant="h6" gutterBottom color="primary.main">
              Load Saved Data
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Load previously saved transactions, tags, and rules.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleLoadFromDatabase}
              disabled={isLoadingFromDatabase}
              startIcon={
                isLoadingFromDatabase ? <CircularProgress size={16} /> : null
              }
            >
              {isLoadingFromDatabase ? "Loading saved data..." : "Load saved data"}
            </Button>
          </Paper>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="textSecondary">
              OR
            </Typography>
          </Divider>
        </>
      )}

      <Typography variant="h6" gutterBottom>
        Upload New CSV File
      </Typography>

      <Paper
        {...getRootProps()}
        sx={{
          p: 6,
          textAlign: "center",
          backgroundColor: isDragActive ? "action.hover" : "background.paper",
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "divider",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
            borderColor: "primary.main",
          },
        }}
      >
        <input {...getInputProps()} />
        {getDropzoneMessage()}
      </Paper>

      {/* Validation Status and Results */}
      {isProcessing && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Processing file...
          </Typography>
        </Box>
      )}

      {validationStatus === "invalid" && validationErrors.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Validation failed. Please fix the following issues:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        </Box>
      )}

      {validationStatus === "valid" && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">
              File validated successfully. {transactionCount} transactions
              found.
            </Typography>
            {transactionCount > 500 && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
                Warning: Large number of transactions may slow down the page.
                Consider breaking the file into multiple smaller files (under
                500 transactions each).
              </Typography>
            )}
          </Alert>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleProceed}
              size="large"
            >
              Proceed to Tag {transactionCount} Transactions
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
